/**
 * UsersTab
 * Full user management functionality embedded in dashboard tab
 * Optimized for web and mobile UX
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import api from '../../../services/api';
import Spinner from '../../common/Spinner';
import Modal from '../../common/Modal';
import BottomSheet from '../../common/BottomSheet';
import UserTable from '../../users/UserTable';
import UserForm from '../../users/UserForm';
import UserFilters from '../../users/UserFilters';
import ResignModal from '../../users/ResignModal';
import StickySearchBar from '../../users/StickySearchBar';
import StatsOverview from '../../users/StatsOverview';
import { SalesHierarchy, DesignHierarchy, SiteTeamHierarchy } from '../../users/HierarchyView';
import { toast } from 'react-toastify';
import { USER_MANAGEMENT_ROLES, HIDDEN_UI_ROLES } from '../../../utils/rolePermissions';
import { useMediaQuery } from '../../../hooks/useMediaQuery';

export default function UsersTab() {
  const { user: currentUser } = useAuth();
  const isMobile = useMediaQuery('(max-width: 767px)');

  const [users, setUsers] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [managers, setManagers] = useState([]);
  const [sids, setSids] = useState([]);
  const [siteManagers, setSiteManagers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formLoading, setFormLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showResignModal, setShowResignModal] = useState(false);
  const [resigningUser, setResigningUser] = useState(null);
  const [resignLoading, setResignLoading] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [reactivatingUser, setReactivatingUser] = useState(null);
  const [reactivateLoading, setReactivateLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resettingUser, setResettingUser] = useState(null);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [viewMode, setViewMode] = useState('hierarchy'); // 'hierarchy' or 'table'
  const [showFilters, setShowFilters] = useState(false); // For filter panel toggle
  const [filters, setFilters] = useState({
    role: '',
    leaderId: '',
    managerId: '',
    sidId: '',
    search: ''
  });

  // Fetch users on mount
  useEffect(() => {
    fetchUsers();
  }, []);

  // Apply filters whenever users or filters change
  useEffect(() => {
    applyFilters();
  }, [users, filters]);

  // Check if any filter is active (excluding search)
  const hasActiveFilter = filters.role || filters.leaderId || filters.managerId || filters.sidId || filters.search;

  // Count active filters (excluding search - search is always visible in sticky bar)
  const activeFilterCount = [filters.role, filters.leaderId, filters.managerId, filters.sidId].filter(Boolean).length;

  // Handle search change from sticky bar
  const handleSearchChange = (value) => {
    setFilters(prev => ({ ...prev, search: value }));
  };

  // Toggle filter panel
  const handleFilterToggle = () => {
    setShowFilters(prev => !prev);
  };

  // Auto-switch to table view when filter is applied
  useEffect(() => {
    if (hasActiveFilter) {
      setViewMode('table');
    }
  }, [hasActiveFilter]);

  async function fetchUsers() {
    try {
      setLoading(true);
      const response = await api.get('/users');
      setUsers(response.data.users);

      // Extract active managers for filters and form
      const managersData = response.data.users.filter(u => u.role === 'manager' && !u.isResigned);
      setManagers(managersData);

      // Extract active leaders for filters and form
      const leadersData = response.data.users.filter(u => u.role === 'leader' && !u.isResigned);
      setLeaders(leadersData);

      // Extract active SIDs for filters and form
      const sidsData = response.data.users.filter(u => u.role === 'sid' && !u.isResigned);
      setSids(sidsData);

      // Extract active site managers for form
      const siteManagersData = response.data.users.filter(u => u.role === 'site_manager' && !u.isResigned);
      setSiteManagers(siteManagersData);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }

  function applyFilters() {
    let filtered = [...users];

    // Filter out hidden UI roles (system_admin) - they shouldn't appear in the user list
    filtered = filtered.filter(u => !HIDDEN_UI_ROLES.includes(u.role));

    // Role filter
    if (filters.role) {
      filtered = filtered.filter(u => u.role === filters.role);
    }

    // Manager filter (for leaders)
    if (filters.managerId) {
      filtered = filtered.filter(u => u.managerId === filters.managerId);
    }

    // Leader filter (for agents/telemarketers)
    if (filters.leaderId) {
      filtered = filtered.filter(u => u.leaderId === filters.leaderId);
    }

    // SID filter (for design team members)
    if (filters.sidId) {
      filtered = filtered.filter(u => u.sidId === filters.sidId);
    }

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(u =>
        u.fullName.toLowerCase().includes(searchLower) ||
        u.email.toLowerCase().includes(searchLower) ||
        (u.nickname && u.nickname.toLowerCase().includes(searchLower))
      );
    }

    setFilteredUsers(filtered);
  }

  function handleCreateUser() {
    setEditingUser(null);
    setShowModal(true);
  }

  function handleEditUser(user) {
    setEditingUser(user);
    setShowModal(true);
  }

  function handleResignUser(user) {
    setResigningUser(user);
    setShowResignModal(true);
  }

  async function handleConfirmResign(resignDate) {
    try {
      setResignLoading(true);
      await api.patch(`/users/${resigningUser.id}`, {
        isResigned: true,
        resignedDate: resignDate
      });
      toast.success('User marked as resigned successfully');
      setShowResignModal(false);
      setResigningUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to resign user');
    } finally {
      setResignLoading(false);
    }
  }

  function handleReactivateUser(user) {
    setReactivatingUser(user);
    setShowReactivateModal(true);
  }

  async function handleConfirmReactivate() {
    try {
      setReactivateLoading(true);
      await api.patch(`/users/${reactivatingUser.id}`, { isResigned: false });
      toast.success(`${reactivatingUser.nickname || reactivatingUser.fullName} reactivated successfully`);
      setShowReactivateModal(false);
      setReactivatingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reactivate user');
    } finally {
      setReactivateLoading(false);
    }
  }

  function handleDeleteUser(user) {
    setDeletingUser(user);
    setShowDeleteModal(true);
  }

  async function handleConfirmDelete() {
    try {
      setDeleteLoading(true);
      await api.delete(`/users/${deletingUser.id}`);
      toast.success('User deleted successfully');
      setShowDeleteModal(false);
      setDeletingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete user');
    } finally {
      setDeleteLoading(false);
    }
  }

  function handleResetPassword(user) {
    setResettingUser(user);
    setShowResetPasswordModal(true);
  }

  async function handleConfirmResetPassword() {
    try {
      setResetPasswordLoading(true);
      await api.post(`/users/${resettingUser.id}/reset-password`);
      toast.success('Password reset to IC number');
      setShowResetPasswordModal(false);
      setResettingUser(null);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reset password');
    } finally {
      setResetPasswordLoading(false);
    }
  }


  async function handleSubmitUser(formData) {
    try {
      setFormLoading(true);

      if (editingUser) {
        // Update existing user
        await api.patch(`/users/${editingUser.id}`, formData);
        toast.success('User updated successfully');
      } else {
        // Create new user
        await api.post('/users', formData);
        toast.success('User created successfully');
      }

      setShowModal(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save user');
    } finally {
      setFormLoading(false);
    }
  }

  function handleCloseModal() {
    setShowModal(false);
    setEditingUser(null);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <>
      {/* Sticky Search Bar with Search, Filters, View Toggle, and Create Button */}
      <StickySearchBar
        searchValue={filters.search}
        onSearchChange={handleSearchChange}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        hasActiveFilter={hasActiveFilter}
        filterCount={activeFilterCount}
        onFilterToggle={handleFilterToggle}
        onCreateUser={handleCreateUser}
        canCreateUser={USER_MANAGEMENT_ROLES.includes(currentUser?.role)}
        disabled={loading}
      />

      {/* Collapsible Stats Overview */}
      <StatsOverview users={users} />

      {/* Filter Panel - Desktop Dropdown or Mobile Bottom Sheet */}
      {isMobile ? (
        <BottomSheet
          isOpen={showFilters}
          onClose={() => setShowFilters(false)}
          title="Filter Users"
        >
          <div className="p-4">
            <UserFilters
              filters={filters}
              onFilterChange={setFilters}
              leaders={leaders}
              managers={managers}
              sids={sids}
              embedded={true}
              hideSearch={true}
              hideHeader={true}
            />
            <div className="mt-4 flex gap-3">
              <button
                onClick={() => {
                  setFilters({ role: '', leaderId: '', managerId: '', sidId: '', search: filters.search });
                  setShowFilters(false);
                }}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Clear All
              </button>
              <button
                onClick={() => setShowFilters(false)}
                className="flex-1 px-4 py-2.5 bg-tenq-gold text-white rounded-lg hover:bg-tenq-gold-dark transition-colors"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </BottomSheet>
      ) : (
        showFilters && (
          <div className="mb-4 animate-slideDown">
            <UserFilters
              filters={filters}
              onFilterChange={setFilters}
              leaders={leaders}
              managers={managers}
              sids={sids}
              hideSearch={true}
            />
          </div>
        )
      )}

      {/* Users Grouped by Department */}
      <div className="space-y-6">
        {/* Management - Always Table View (admin, director, manager) */}
        {(() => {
          const managementUsers = filteredUsers.filter(u => ['system_admin', 'admin', 'director', 'manager'].includes(u.role))
            .sort((a, b) => {
              const roleOrder = { system_admin: 0, admin: 1, director: 2, manager: 3 };
              return roleOrder[a.role] - roleOrder[b.role];
            });
          return managementUsers.length > 0 && (
            <div className="card">
              <div className="mb-4 flex items-center">
                <div className="bg-red-100 p-2 rounded-lg mr-3">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h2 className="text-xl font-display font-bold text-gray-900">
                  Management ({managementUsers.length})
                </h2>
              </div>
              <UserTable
                users={managementUsers}
                onEdit={handleEditUser}
                onResign={handleResignUser}
                onReactivate={handleReactivateUser}
                onDelete={handleDeleteUser}
                currentUserId={currentUser?.id}
                isSystemAdmin={currentUser?.role === 'system_admin'}
              />
            </div>
          );
        })()}

        {/* Sales Team */}
        {(() => {
          const salesUsers = filteredUsers.filter(u =>
            ['manager', 'leader', 'agent', 'telemarketer'].includes(u.role)
          );
          const salesCount = salesUsers.filter(u => ['leader', 'agent', 'telemarketer'].includes(u.role)).length;

          return salesCount > 0 && (
            <div className="card">
              <div className="mb-4 flex items-center">
                <div className="bg-blue-100 p-2 rounded-lg mr-3">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h2 className="text-xl font-display font-bold text-gray-900">
                  Sales Team ({salesCount})
                </h2>
              </div>

              {viewMode === 'hierarchy' ? (
                <SalesHierarchy
                  users={salesUsers}
                  onEdit={handleEditUser}
                  onResign={handleResignUser}
                  onReactivate={handleReactivateUser}
                  onDelete={handleDeleteUser}
                  currentUserId={currentUser?.id}
                  isSystemAdmin={currentUser?.role === 'system_admin'}
                />
              ) : (
                <UserTable
                  users={salesUsers.filter(u => ['leader', 'agent', 'telemarketer'].includes(u.role))
                    .sort((a, b) => {
                      const roleOrder = { leader: 1, agent: 2, telemarketer: 3 };
                      return roleOrder[a.role] - roleOrder[b.role];
                    })}
                  onEdit={handleEditUser}
                  onResign={handleResignUser}
                  onReactivate={handleReactivateUser}
                  onDelete={handleDeleteUser}
                  currentUserId={currentUser?.id}
                  isSystemAdmin={currentUser?.role === 'system_admin'}
                />
              )}
            </div>
          );
        })()}

        {/* Design Team */}
        {(() => {
          const designUsers = filteredUsers.filter(u =>
            ['sid', 'asid', 'designer', 'draughter_2d', 'draughter_site'].includes(u.role)
          );

          return designUsers.length > 0 && (
            <div className="card">
              <div className="mb-4 flex items-center">
                <div className="bg-tenq-gold/10 p-2 rounded-lg mr-3">
                  <svg className="w-5 h-5 text-tenq-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                  </svg>
                </div>
                <h2 className="text-xl font-display font-bold text-gray-900">
                  Design Team ({designUsers.length})
                </h2>
              </div>

              {viewMode === 'hierarchy' ? (
                <DesignHierarchy
                  users={designUsers}
                  onEdit={handleEditUser}
                  onResign={handleResignUser}
                  onReactivate={handleReactivateUser}
                  onDelete={handleDeleteUser}
                  currentUserId={currentUser?.id}
                  isSystemAdmin={currentUser?.role === 'system_admin'}
                />
              ) : (
                <UserTable
                  users={designUsers.sort((a, b) => {
                    const roleOrder = { sid: 1, asid: 2, designer: 3, draughter_2d: 4, draughter_site: 5 };
                    return roleOrder[a.role] - roleOrder[b.role];
                  })}
                  onEdit={handleEditUser}
                  onResign={handleResignUser}
                  onReactivate={handleReactivateUser}
                  onDelete={handleDeleteUser}
                  currentUserId={currentUser?.id}
                  isSystemAdmin={currentUser?.role === 'system_admin'}
                />
              )}
            </div>
          );
        })()}

        {/* Support Team - Always Table View */}
        {(() => {
          const supportUsers = filteredUsers.filter(u =>
            ['quotation_team', 'legal_team'].includes(u.role)
          ).sort((a, b) => {
            const roleOrder = { quotation_team: 1, legal_team: 2 };
            return roleOrder[a.role] - roleOrder[b.role];
          });
          return supportUsers.length > 0 && (
            <div className="card">
              <div className="mb-4 flex items-center">
                <div className="bg-teal-100 p-2 rounded-lg mr-3">
                  <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                </div>
                <h2 className="text-xl font-display font-bold text-gray-900">
                  Support Team ({supportUsers.length})
                </h2>
              </div>
              <UserTable
                users={supportUsers}
                onEdit={handleEditUser}
                onResign={handleResignUser}
                onReactivate={handleReactivateUser}
                onDelete={handleDeleteUser}
                currentUserId={currentUser?.id}
                isSystemAdmin={currentUser?.role === 'system_admin'}
              />
            </div>
          );
        })()}

        {/* Site Team */}
        {(() => {
          const siteUsers = filteredUsers.filter(u =>
            ['operation_head', 'site_manager', 'site_supervisor'].includes(u.role)
          ).sort((a, b) => {
            const roleOrder = { operation_head: 1, site_manager: 2, site_supervisor: 3 };
            return roleOrder[a.role] - roleOrder[b.role];
          });
          return siteUsers.length > 0 && (
            <div className="card">
              <div className="mb-4 flex items-center">
                <div className="bg-cyan-100 p-2 rounded-lg mr-3">
                  <svg className="w-5 h-5 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h2 className="text-xl font-display font-bold text-gray-900">
                  Site Team ({siteUsers.length})
                </h2>
              </div>

              {viewMode === 'hierarchy' ? (
                <SiteTeamHierarchy
                  users={siteUsers}
                  onEdit={handleEditUser}
                  onResign={handleResignUser}
                  onReactivate={handleReactivateUser}
                  onDelete={handleDeleteUser}
                  currentUserId={currentUser?.id}
                  isSystemAdmin={currentUser?.role === 'system_admin'}
                />
              ) : (
                <UserTable
                  users={siteUsers}
                  onEdit={handleEditUser}
                  onResign={handleResignUser}
                  onReactivate={handleReactivateUser}
                  onDelete={handleDeleteUser}
                  currentUserId={currentUser?.id}
                  isSystemAdmin={currentUser?.role === 'system_admin'}
                />
              )}
            </div>
          );
        })()}

        {/* No Results Message */}
        {filteredUsers.length === 0 && (
          <div className="card">
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
              <p className="mt-1 text-sm text-gray-500">Try adjusting your filters or create a new user.</p>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit User Modal */}
      <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingUser ? 'Edit User' : 'Create New User'}
        size="md"
      >
        <UserForm
          user={editingUser}
          leaders={leaders}
          managers={managers}
          sids={sids}
          siteManagers={siteManagers}
          currentUser={currentUser}
          onSubmit={handleSubmitUser}
          onCancel={handleCloseModal}
          onResetPassword={handleResetPassword}
          loading={formLoading}
        />
      </Modal>

      {/* Resign User Modal */}
      <ResignModal
        isOpen={showResignModal}
        onClose={() => setShowResignModal(false)}
        onConfirm={handleConfirmResign}
        user={resigningUser}
        loading={resignLoading}
      />

      {/* Reactivate User Modal */}
      <Modal
        isOpen={showReactivateModal}
        onClose={() => setShowReactivateModal(false)}
        title="Reactivate User"
        size="sm"
      >
        <div className="p-4">
          <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-green-100">
            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-center text-gray-900 mb-2">
            Reactivate {reactivatingUser?.nickname || reactivatingUser?.fullName}?
          </h3>
          <p className="text-sm text-center text-gray-500 mb-6">
            This user will be marked as active again and will be able to log in.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => setShowReactivateModal(false)}
              disabled={reactivateLoading}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmReactivate}
              disabled={reactivateLoading}
              className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {reactivateLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Reactivating...
                </>
              ) : (
                'Reactivate User'
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete User Modal (System Admin Only) */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete User"
        size="sm"
      >
        <div className="p-4">
          <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-red-100">
            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-center text-gray-900 mb-2">
            Delete {deletingUser?.nickname || deletingUser?.fullName}?
          </h3>
          <p className="text-sm text-center text-gray-500 mb-6">
            This action cannot be undone. The user will be permanently removed from the system.
            {deletingUser?.isResigned === false && (
              <span className="block mt-2 text-amber-600 font-medium">
                Warning: This user is still active. Consider resigning them instead.
              </span>
            )}
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => setShowDeleteModal(false)}
              disabled={deleteLoading}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmDelete}
              disabled={deleteLoading}
              className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {deleteLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Deleting...
                </>
              ) : (
                'Delete User'
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        isOpen={showResetPasswordModal}
        onClose={() => setShowResetPasswordModal(false)}
        title="Reset Password"
        size="sm"
      >
        <div className="p-4">
          <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-blue-100">
            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-center text-gray-900 mb-2">
            Reset password for {resettingUser?.nickname || resettingUser?.fullName}?
          </h3>
          <p className="text-sm text-center text-gray-500 mb-4">
            The password will be reset to this user's IC number (digits only):
          </p>
          <div className="text-center mb-6">
            <span className="inline-block px-3 py-1.5 rounded-lg bg-gray-100 font-mono text-gray-900 tracking-wide">
              {(resettingUser?.mykadIC || '').replace(/\D/g, '') || '—'}
            </span>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowResetPasswordModal(false)}
              disabled={resetPasswordLoading}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmResetPassword}
              disabled={resetPasswordLoading}
              className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {resetPasswordLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Resetting...
                </>
              ) : (
                'Reset Password'
              )}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
